import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { DropDown } from "@swa-ui/core";
import { classNames, getUniqueId } from "@swa-ui/string";

import styles from "./ChildAgeSelector.module.scss";

const MAX_AGE = 17;
const MAX_ITEMS_TO_DISPLAY = 4;

/**
 * ChildAgeSelector is a simple helper component to allow a traveler to select child ages.
 */
export const ChildAgeSelector = (props) => {
  const { ageCriteria = [], className, id, label, numberOfSelectors = 1, onChange } = props;
  const [currentAgeCriteria, setCurrentAgeCriteria] = useState();
  const ageValues = useMemo(() => initializeSelectionOptions(), []);
  const uniqueId = useRef(id || getUniqueId("child-age-selector-id"));

  useEffect(() => {
    if (ageCriteria.length) {
      setCurrentAgeCriteria([...ageCriteria]);
    }
  }, [ageCriteria]);

  return (
    <ul className={classNames(className)} id={id}>
      {(currentAgeCriteria || ageCriteria).slice(0, numberOfSelectors).map(renderNumberOptions)}
    </ul>
  );

  function renderNumberOptions(criteria, index) {
    return (
      <li className={styles.option} data-unstable key={criteria.key}>
        <label className={styles.label} htmlFor={`${uniqueId.current}-${index}`}>
          {label} {index + 1}
        </label>
        <DropDown {...getDropDownProps(criteria, index)} />
      </li>
    );
  }

  function getDropDownProps(criteria, index) {
    const { maxAge = MAX_AGE, minAge = 0, name } = criteria;

    return {
      className: styles.childAgeSelectors,
      id: `${uniqueId.current}-${index}`,
      list: ageValues.slice(minAge, maxAge + 1),
      maxItemsToDisplay: MAX_ITEMS_TO_DISPLAY,
      name,
      onChange: handleChange.bind(this, index),
      onTriggerClick: (event) => event.stopPropagation(),
      size: "small",
      value: (currentAgeCriteria || ageCriteria)[index].age,
    };
  }

  function handleChange(index, event) {
    const newAgeCriteria = [...ageCriteria];

    newAgeCriteria[index].age = event.target.value;
    onChange?.(getAges(newAgeCriteria));
  }

  function initializeSelectionOptions() {
    return Array.from({ length: MAX_AGE + 1 }, (_, index) => ({
      displayValue: index ? `${index}` : "< 1",
      label: <div className={styles.optionLabel}>{index ? index : <>&lt; 1</>}</div>,
      value: index,
    }));
  }

  function getAges(workingAgeCriteria) {
    return workingAgeCriteria.map((criteria) => criteria.age);
  }
};

ChildAgeSelector.propTypes = {
  /** Information needed to display each child age DropDown selector. */
  ageCriteria: PropTypes.arrayOf(
    PropTypes.shape({
      age: PropTypes.number.isRequired,
      key: PropTypes.string,
      maxAge: PropTypes.number,
      minAge: PropTypes.number,
      name: PropTypes.string,
    })
  ),

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Unique id to be assigned to DOM element. */
  id: PropTypes.string,

  /**
   * Text to be rendered before each DropDown age selector. label will typically be set to "Child
   * age".
   */
  label: PropTypes.string.isRequired,

  /** Count of age selectors to render. Nothing will be rendered unless this is non-zero. */
  numberOfSelectors: PropTypes.number,

  /** Callback to inform when traveler changes the count of a age DropDown. */
  onChange: PropTypes.func,
};
